import { ReactElement, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useProductsAnalytics } from '@client/analytics/context/products/ProductsAnalyticsContext';
import { ProductGroup } from '@client/common/components/ProductGroup/ProductGroup';
import { chunkArray } from '@client/common/utils/array/array';
import { getIsLoggedIn } from '@client/context/redux/redux';
import { useTranslate } from '@client/i18n/hooks';
import { breakpoints, Props, SLIDER_PARAMS } from '@client/product/components/ProductData/SimilarProducts/SimilarProducts.types';
import { selectSimilarProducts, selectSimilarProductsRecords } from '@client/product/redux/similarProducts/redux';
import { ProductList } from '@lib/components/ProductList';
import { DEVICE_TYPE } from '@lib/utils/device';
import classNames from 'classnames';
import { Slider, SLIDER_STYLE_TYPE } from 'b2b-components/theme/components/Slider';
import '@client/product/components/ProductData/SimilarProducts/SimilarProducts.scss';

const CHUNK_SIZE = 4;

export const SimilarProducts = ({
  deviceType,
}: Props): ReactElement | null => {
  const t = useTranslate();
  const { analyzeProductsListView, analyzeProductAddToCart, analyzeProductClick } = useProductsAnalytics();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const { data: similarProducts } = useSelector(selectSimilarProducts);
  const skus = Object.keys(similarProducts);
  const getSimilarProductsRecords = useSelector(selectSimilarProductsRecords);

  const hasSlider = skus.length > 4
    || (deviceType === DEVICE_TYPE.phone && skus.length > 1)
    || (deviceType === DEVICE_TYPE.tablet && skus.length > 2);

  useEffect(() => {
    if (skus?.length > 0) {
      analyzeProductsListView();
    }
  }, []);

  if (!skus.length) {
    return null;
  }

  const renderSlides = (): ReactElement[] => (
    chunkArray(skus, CHUNK_SIZE)
      .map((chunk: string[], index) => (
        <ProductGroup
          skus={chunk}
          productTiles={getSimilarProductsRecords(chunk)}
          isForSlider={hasSlider}
          isFromContentList={false}
          onProductClick={analyzeProductClick}
          onProductAddToCart={analyzeProductAddToCart}
          absolutePosition={index * CHUNK_SIZE}
          {...hasSlider && { className: 'SimilarProducts' }}
          key={`${chunk}-${index}`}
        />
      ))
  );

  return (
    <div className="SimilarProducts-Container">
      <h3
        className={classNames(
          'SimilarProducts-ContainerHeader',
          { 'SimilarProducts-ContainerHeader--Slider': (hasSlider && deviceType === DEVICE_TYPE.desktop) },
        )}
      >
        {t('Similar Products')}
      </h3>
      {hasSlider ? (
        <div className='cds-Slider cds-Slider--WithMargin'>
          <Slider
            {...SLIDER_PARAMS.xs}
            breakpoints={breakpoints}
            isLoop={false}
            isAutoplay={false}
            styleType={SLIDER_STYLE_TYPE.default}
            className='SimilarProducts-SliderContainer'
            containerClassName='SimilarProducts-Slider'
          >
            {renderSlides()}
          </Slider>
        </div>
      ) : (
        <ProductList text={{
          sku: t('SKU'),
          manufacturerSku: t('Manufacturer Product ID'),
          title: t('Title'),
          price: isLoggedIn
            ? t('Price<br />(excl. VAT)')
            : t('Price<br />(incl. VAT)'),
          stock: t('Stock'),
        }}>
          {renderSlides()}
        </ProductList>
      )}
    </div>
  );
};
